import type {
  ModulationQuizQuestion as Question,
  ModulationQuizVerdict,
} from '@tunasong/music-lib'
import { Button } from '@tunasong/ui-lib'
import { type FC } from 'react'
import { useTheoryCopy } from '../theory-copy/use-theory-copy.js'
import { ModulationPhaseDiagram } from './modulation-phase-diagram.js'
import styles from './modulation-quiz-feedback.module.css'

export interface ModulationQuizFeedbackProps {
  question: Question
  verdict: ModulationQuizVerdict
  onNext: () => void
}

/**
 * Quiz feedback surface — reveals the technique, source/destination keys, the
 * full 3-block diagram (no highlight), and a pedagogical tip drawn from the
 * `modulation-quiz-feedback` lexicon entry.
 *
 * Spec: docs/superpowers/specs/2026-05-26-modulation-lab-v1-design.md §6
 */
export const ModulationQuizFeedback: FC<ModulationQuizFeedbackProps> = props => {
  const { question, verdict, onNext } = props
  const feedbackCopy = useTheoryCopy('modulation-quiz-feedback')

  return (
    <div className={styles.feedback}>
      <div
        className={verdict.correct ? styles.correctLabel : styles.incorrectLabel}
        aria-live="polite"
      >
        {verdict.correct ? 'Correct' : 'Incorrect'} — technique was {question.acceptedAnswer}
      </div>
      <ModulationPhaseDiagram plan={question.plan} currentPhase={null} />
      {feedbackCopy?.body ? <p className={styles.tip}>{feedbackCopy.body}</p> : null}
      <Button type="button" onClick={onNext}>
        Next question
      </Button>
    </div>
  )
}
